/* eslint-disable camelcase */
import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { FormikProps } from 'formik';
import {
  withInject,
  Action,
} from '@piwikpro/platform';
import {
  Text,
  Form,
  Stack,
  Card,
  Spinner,
  Divider,
  withTranslation,
  Link,
  Trans,
  Layout,
  TFunction,
} from '@piwikpro/ui-components';
import { FormValidator } from '@piwikpro/form-crate';
import { UnsavedChangesTracker as UnsavedChangesTrackerComponent } from '@piwikpro/form-crate/components/UnsavedChangesTracker';
import { ContainerSettings } from '../../ContainerSettings';
import FailMessage from '../FailMessage';
import { ContainerSettingsState } from '../../redux';
import { DomainSetting } from '../../redux/appDomainSettings';

const mapStateToProps = (state: ContainerSettingsState) => ({
  isLoading: state.containerSettings.app.scrollTracking.isLoading,
  failed: state.containerSettings.app.scrollTracking.failed,
  value: state.containerSettings.app.scrollTracking.value,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  containerSettings: ContainerSettings
  UnsavedChangesTracker: typeof UnsavedChangesTrackerComponent
  validation: FormValidator
}

export interface Props extends ReduxProps, InjectedProps {
  appId: string
  controlWrapper?: typeof Action
  t: TFunction
}

export interface IFormValues {
  scrollEnabled: boolean
  scrollThresholds: number[]
}

export class ScrollTracking extends React.Component<Props> {
  private settingName: DomainSetting = 'automatic_scroll_tracking';

  constructor(props: Props) {
    super(props);

    this.handleSwitcherSubmit = this.handleSwitcherSubmit.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
  }

  async componentDidMount() {
    const { containerSettings, appId } = this.props;

    try {
      await containerSettings.getAppSetting(appId, this.settingName);
      // eslint-disable-next-line no-empty
    } catch { }
  }

  async handleFormSubmit(values: IFormValues, { setSubmitting }: FormikProps<IFormValues>) {
    const { containerSettings, appId, value } = this.props;

    try {
      await containerSettings.updateAppSetting(
        appId,
        this.settingName,
        {
          value: {
            is_enabled: value!.is_enabled,
            thresholds: values.scrollThresholds,
          },
        },
      );
      // eslint-disable-next-line no-empty
    } catch { }

    setSubmitting(false);
  }

  async handleSwitcherSubmit(
    values: IFormValues,
    { setFieldValue, setSubmitting }: FormikProps<IFormValues>,
  ) {
    const { containerSettings, appId, value } = this.props;

    try {
      await containerSettings.updateAppSetting(
        appId,
        this.settingName,
        {
          value: {
            thresholds: value!.thresholds,
            is_enabled: values.scrollEnabled,
          },
        },
      );
    } catch {
      setFieldValue('scrollEnabled', !values.scrollEnabled, false);
    }

    setSubmitting(false);
  }

  render() {
    const {
      validation,
      value,
      isLoading,
      failed,
      controlWrapper,
      UnsavedChangesTracker,
      t,
    } = this.props;

    const ActionWrapper = controlWrapper || Action;

    return (
      <Layout>
        <Layout.AnnotatedSection
          title={t('automaticScrollTracking.section.header')}
        />
        <Layout.Column dominant>
          {isLoading && (
            <Card>
              <Card.Section>
                <Spinner />
              </Card.Section>
            </Card>
          )}
          {failed && <FailMessage />}
          {value && (
            <Card>
              <Card.Section>
                <Stack wrap verticalAlignment="center">
                  <Stack.Item fill>
                    <Stack vertical spacing="narrow">
                      <Stack.Item>
                        <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">
                          {t('automaticScrollTracking.section.title')}
                        </Text>
                      </Stack.Item>
                      <Stack.Item>
                        <Text as="p">
                          <Trans
                            i18nKey="container-settings:automaticScrollTracking.section.description"
                            components={[
                              <Link
                                href="https://help.piwik.pro/support/questions/how-can-i-set-up-reporting-for-page-scrolls/"
                              >
                                help center article
                              </Link>,
                            ]}
                          />
                        </Text>
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>
                  <Stack.Item>
                    <ActionWrapper.AutoSavedSwitcherControl
                      id="container-settings-automatic-scroll-tracking-switcher"
                      initialValue={value.is_enabled}
                      name="scrollEnabled"
                      onSubmit={this.handleSwitcherSubmit}
                    />
                  </Stack.Item>
                </Stack>
              </Card.Section>
              {value.is_enabled && (
                <Form
                  id="container-settings-scroll-tracking-form"
                  initialValues={{
                    scrollThresholds: value.thresholds,
                  }}
                  validationSchema={validation.createValidationSchema(validators => ({
                    scrollThresholds: validators.containerSettings.scrollThresholds,
                  }))}
                  onSubmit={this.handleFormSubmit}
                  render={({
                    values,
                    setFieldValue,
                    errors,
                    resetForm,
                    isValid,
                  }: FormikProps<IFormValues>) => (
                    <Card.Section>
                      <Card>
                        <Stack vertical spacing="narrow">
                          <Stack.Item>
                            <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">{t('automaticScrollTracking.thresholds.form.title')}</Text>
                          </Stack.Item>
                          <Stack.Item />
                        </Stack>
                        <Card.Section subdued>
                          <Stack vertical>
                            <Stack.Item>
                              <Stack vertical>
                                {values.scrollThresholds.map(
                                  (threshold: number, index: number) => (
                                    <Stack.Item>
                                      <Stack spacing="extraNarrow">
                                        <Stack.Item>
                                          <ActionWrapper.NumberControl
                                            name="scrollThreshold"
                                            type="number"
                                            units="%"
                                            onBlur={() => {}}
                                            error={
                                              errors.scrollThresholds
                                              && errors.scrollThresholds[index]
                                            }
                                            value={threshold}
                                            onChange={(event) => {
                                              const temp = [...values.scrollThresholds];

                                              temp[index] = event.target.value as number;

                                              setFieldValue('scrollThresholds', temp);
                                            }}
                                          />
                                        </Stack.Item>
                                        <Stack.Item>
                                          <ActionWrapper.Button
                                            icon="cross"
                                            dataId="remove-scroll-point-button"
                                            appearance="subdued"
                                            onClick={() => {
                                              const temp = [...values.scrollThresholds];

                                              temp.splice(index, 1);

                                              setFieldValue('scrollThresholds', temp);
                                            }}
                                          />
                                        </Stack.Item>
                                      </Stack>
                                    </Stack.Item>
                                  ),
                                )}
                              </Stack>
                            </Stack.Item>
                            <Stack.Item>
                              <ActionWrapper.Button
                                icon="plus"
                                dataId="add-scroll-point-button"
                                appearance="simple"
                                text={t('automaticScrollTracking.thresholds.form.addScrollPoint')}
                                onClick={() => setFieldValue('scrollThresholds', [...values.scrollThresholds, 0])}
                              />
                            </Stack.Item>
                          </Stack>
                        </Card.Section>
                      </Card>
                      <Divider />
                      <Stack spacing="narrow">
                        <Stack.Item>
                          <Form.Submit text={t('automaticScrollTracking.thresholds.form.submitForm')} />
                          <UnsavedChangesTracker render={Api => <Api.UnsavedChangesModal />} type={isValid ? 'threeOptions' : 'twoOptions'} />
                        </Stack.Item>
                        <Stack.Item>
                          <Form.Cancel onClick={() => resetForm()} dataId="scroll-points-cancel-button" />
                        </Stack.Item>
                      </Stack>
                    </Card.Section>
                  )}
                />
              )}
            </Card>
          )}
        </Layout.Column>
      </Layout>
    );
  }
}

export default connector(withInject<InjectedProps>({
  containerSettings: 'ContainerSettingsCrate.ContainerSettings',
  UnsavedChangesTracker: 'FormCrate.components.UnsavedChangesTracker',
  validation: 'FormCrate.validation',
})(withTranslation(['container-settings', 'administration'])(ScrollTracking)));
